/**
 * Demo mode — SKE-4166 "public interactive approvals demo".
 *
 * A single public link (try.skedsocial.com/approvals) points anonymous ad
 * traffic at this same approver SPA so visitors can experience the approval
 * product without logging in.
 *
 * ACTIVATION
 * ----------
 * Demo mode is detected from `window.location` (not a `<Route>`) so it is known
 * before the router mounts and before the first API call is issued. Any of
 * these turns it on:
 *
 *   1. `VITE_DEMO_MODE=true`         — build-time switch, for a dedicated
 *                                      demo-only deployment.
 *   2. `/approvals` top-level path   — the ad landing URL. NOTE: serving this
 *                                      pathname needs a host SPA-rewrite
 *                                      (`/approvals` → index.html). The S3 +
 *                                      CloudFront host does NOT honour the
 *                                      Netlify `public/_redirects` file, so that
 *                                      rewrite is configured on the distribution
 *                                      (tracked separately as infra).
 *   3. `?demo=1` query param         — escape hatch so QA can force demo mode
 *                                      on any host without a rebuild.
 *   4. `#/review/demo` sentinel hash — installed by `ensureDemoRoute()` below,
 *                                      and also a recognised signal so demo mode
 *                                      survives a reload/refresh even if (2)/(3)
 *                                      are lost.
 *
 * A real reviewer link (collab.skedsocial.com/#/review/{slugId}) matches none
 * of these, so the reviewer path is completely untouched.
 *
 * ROUTING
 * -------
 * Rather than teach Homepage about a second route shape, demo mode reuses the
 * existing `#/review/{key}` route with a sentinel key (`demo`). `ensureDemoRoute()`
 * installs that hash before React mounts, so `getReviewKeyFromURL()` and every
 * downstream `navigate('/review/{key}/posts/{id}')` keep working verbatim. The
 * sentinel is swapped for the real upstream key inside the api adapter — the
 * real key never has to appear in the visitor's address bar.
 */

import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'

/** Sentinel review key that stands in for the demo link in the URL. */
export const DEMO_REVIEW_KEY = 'demo'

/** Hash route the demo lands on. */
export const DEMO_ROUTE = `#/review/${DEMO_REVIEW_KEY}`

const DEMO_PATHNAME_PATTERN = /^\/approvals(\/|$)/

/**
 * True when this page load is the public demo.
 *
 * Reads `window.location` on every call (rather than caching at module load)
 * so tests can drive it with `history.pushState`, and so a same-document
 * navigation can never leave the flag stale.
 */
export const isDemoMode = (): boolean => {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    return true
  }

  const location = globalThis.window?.location
  if (!location) {
    return false
  }

  if (DEMO_PATHNAME_PATTERN.test(location.pathname)) {
    return true
  }

  // The demo installs the `#/review/demo` sentinel hash route. Recognising it
  // keeps demo mode alive across a reload/refresh even when the `?demo=1` (or
  // `/approvals` pathname) activation signal is lost, and makes
  // `#/review/demo/posts/x` deep links resolve as demo. A real reviewer link
  // yields its own key (never the `demo` sentinel), so the reviewer path stays
  // untouched.
  if (getReviewKeyFromURL(location.href) === DEMO_REVIEW_KEY) {
    return true
  }

  // `?demo=1` may sit in the real query string or after the hash route
  // (`#/review/demo?demo=1`), depending on how the link was shared.
  return (
    new URLSearchParams(location.search).get('demo') === '1' ||
    location.hash.includes('demo=1')
  )
}

/**
 * The review key the demo actually reads from: a dedicated **read-only**
 * ExternalApproval link on the seeded `driftline-studio` demo account.
 *
 * When `VITE_DEMO_REVIEW_KEY` is unset (local dev, or before the live link
 * exists) this returns the sentinel key. In `pnpm start:mock` / vitest the MSW
 * handlers match any review key (`reviewKey = '*'` in mocks/handlers.ts), so
 * the demo is fully runnable against the mock fixtures with no live backend.
 */
export const getDemoUpstreamReviewKey = (): string => {
  const configured = import.meta.env.VITE_DEMO_REVIEW_KEY?.trim()

  if (configured) {
    return configured
  }

  warnOnceAboutMissingKey()
  return DEMO_REVIEW_KEY
}

let hasWarnedAboutMissingKey = false
const warnOnceAboutMissingKey = () => {
  if (hasWarnedAboutMissingKey) {
    return
  }
  hasWarnedAboutMissingKey = true
  console.warn(
    '[external-review] VITE_DEMO_REVIEW_KEY is not set — the approvals demo will ' +
      'only load data when MSW mocks are running (pnpm start:mock).'
  )
}

/**
 * Map the sentinel key seen in the URL onto the real upstream key. Any other
 * key (a genuine reviewer link) is returned untouched, so this is safe to call
 * unconditionally.
 */
export const resolveUpstreamReviewKey = (reviewKey: string): string =>
  isDemoMode() && (reviewKey === DEMO_REVIEW_KEY || !reviewKey)
    ? getDemoUpstreamReviewKey()
    : reviewKey

/**
 * Install the demo hash route before React mounts. No-op outside demo mode,
 * and no-op if the visitor already deep-linked into a `#/review/...` route
 * (e.g. `#/review/demo/posts/{id}` after a refresh).
 */
export const ensureDemoRoute = (): void => {
  if (!isDemoMode()) {
    return
  }

  if (globalThis.window?.location.hash.includes('#/review/')) {
    return
  }

  globalThis.window.location.hash = DEMO_ROUTE
}
